<template>
  <header class="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shadow-sm">
    <div class="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
      <!-- Left side -->
      <div class="flex items-center space-x-4">
        <!-- Mobile menu button -->
        <button
          @click="$emit('toggleSidebar')"
          class="lg:hidden p-2 text-gray-400 hover:text-gray-600 dark:text-gray-300 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <Bars3Icon class="w-6 h-6" />
        </button>
        
        <!-- Breadcrumb -->
        <nav class="hidden sm:flex items-center space-x-2 text-sm">
          <router-link
            :to="dashboardLink"
            class="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:text-gray-300 transition-colors"
          >
            Dashboard
          </router-link>
          <ChevronRightIcon v-if="breadcrumbs.length > 0" class="w-4 h-4 text-gray-400" />
          <template v-for="(crumb, index) in breadcrumbs" :key="index">
            <router-link
              v-if="crumb.to"
              :to="crumb.to"
              class="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:text-gray-300 transition-colors"
            >
              {{ crumb.label }}
            </router-link>
            <span v-else class="text-gray-900 dark:text-white font-medium">
              {{ crumb.label }}
            </span>
            <ChevronRightIcon
              v-if="index < breadcrumbs.length - 1"
              class="w-4 h-4 text-gray-400"
            />
          </template>
        </nav>
      </div>
      
      <!-- Right side -->
      <div class="flex items-center space-x-4">
        <!-- Search -->
        <div class="hidden md:block relative">
          <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <MagnifyingGlassIcon class="w-5 h-5 text-gray-400" />
          </div>
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Search... (⌘K)"
            class="w-64 pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            @focus="showSearchResults = true"
            @blur="hideSearchResults"
            @keydown.enter="performSearch"
          />
          
          <!-- Search results dropdown -->
          <Transition name="slide-down">
            <div
              v-if="showSearchResults && searchQuery"
              class="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg py-2 z-50"
            >
              <div v-if="searchResults.length > 0" class="max-h-64 overflow-y-auto">
                <button
                  v-for="result in searchResults"
                  :key="result.id"
                  @click="navigateToResult(result)"
                  class="w-full px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  <div class="flex items-center space-x-3">
                    <component :is="result.icon" class="w-4 h-4 text-gray-400 dark:text-gray-500" />
                    <div>
                      <p class="text-sm font-medium text-gray-900 dark:text-white">{{ result.title }}</p>
                      <p class="text-xs text-gray-500 dark:text-gray-400">{{ result.subtitle }}</p>
                    </div>
                  </div>
                </button>
              </div>
              <div v-else class="px-4 py-2 text-sm text-gray-500 dark:text-gray-400">
                No results found
              </div>
            </div>
          </Transition>
        </div>
        
        <!-- Quick actions -->
        <div class="flex items-center space-x-2">
          <button
            @click="createCampaign"
            class="btn btn-primary btn-sm hidden sm:inline-flex"
          >
            <PlusIcon class="w-4 h-4 mr-1" />
            New Campaign
          </button>
          
          <!-- Mobile quick action -->
          <button
            @click="createCampaign"
            class="sm:hidden p-2 text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-colors"
          >
            <PlusIcon class="w-5 h-5" />
          </button>
        </div>
        
        <!-- Theme Toggle -->
        <ThemeToggle />

        <!-- Language Switcher -->
        <LanguageSwitcher :compact="true" position="right" />

        <!-- Notifications -->
        <div class="relative">
          <button
            @click="showNotifications = !showNotifications"
            class="p-2 text-gray-400 hover:text-gray-600 dark:text-gray-300 hover:bg-gray-100 rounded-lg transition-colors relative"
          >
            <BellIcon class="w-6 h-6" />
            <span
              v-if="unreadCount > 0"
              class="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center"
            >
              {{ unreadCount > 9 ? '9+' : unreadCount }}
            </span>
          </button>

          <!-- Notifications dropdown -->
          <NotificationsDropdown
            v-if="showNotifications"
            @close="showNotifications = false"
          />
        </div>
        
        <!-- User menu -->
        <UserMenu />
      </div>
    </div>
  </header>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAppStore } from '@/stores/app'
import UserMenu from './UserMenu.vue'
import NotificationsDropdown from '../ui/NotificationsDropdown.vue'
import ThemeToggle from '../common/ThemeToggle.vue'
import LanguageSwitcher from '../common/LanguageSwitcher.vue'

import {
  Bars3Icon,
  MagnifyingGlassIcon,
  BellIcon,
  PlusIcon,
  ChevronRightIcon,
  MegaphoneIcon,
  UsersIcon,
  DocumentDuplicateIcon
} from '@heroicons/vue/24/outline'

defineEmits(['toggleSidebar'])

const route = useRoute()
const router = useRouter()
const appStore = useAppStore()

const searchQuery = ref('')
const showSearchResults = ref(false)
const showNotifications = ref(false)
const searchResults = ref([])

const unreadCount = computed(() => appStore.unreadNotifications.length)

// Computed link with locale
const dashboardLink = computed(() => {
  const locale = route.params.locale || 'en'
  return `/${locale}/app/dashboard`
})

// Helper to create route with locale
const withLocale = (routeObj) => {
  const locale = route.params.locale || 'en'
  return { ...routeObj, params: { ...routeObj.params, locale } }
}

// Generate breadcrumbs based on current route
const breadcrumbs = computed(() => {
  const crumbs = []

  if (route.name?.startsWith('campaigns')) {
    crumbs.push({ label: 'Campaigns', to: withLocale({ name: 'campaigns' }) })
    if (route.name === 'campaigns.create') {
      crumbs.push({ label: 'Create Campaign' })
    } else if (route.name === 'campaigns.show') {
      crumbs.push({ label: 'Campaign Details' })
    }
  } else if (route.name?.startsWith('contacts')) {
    crumbs.push({ label: 'Contacts', to: withLocale({ name: 'contacts' }) })
    if (route.name === 'contacts.create') {
      crumbs.push({ label: 'Add Contact' })
    }
  } else if (route.name?.startsWith('templates')) {
    crumbs.push({ label: 'Templates', to: withLocale({ name: 'templates' }) })
    if (route.name === 'templates.create') {
      crumbs.push({ label: 'Create Template' })
    } else if (route.name === 'templates.builder') {
      crumbs.push({ label: 'Template Builder' })
    }
  } else if (route.name?.startsWith('automations')) {
    crumbs.push({ label: 'Automations', to: withLocale({ name: 'automations' }) })
    if (route.name === 'automations.create') {
      crumbs.push({ label: 'Create Automation' })
    }
  } else if (route.name === 'settings') {
    crumbs.push({ label: 'Settings' })
  } else if (route.name === 'profile') {
    crumbs.push({ label: 'Profile' })
  }

  return crumbs
})

// Search functionality
watch(searchQuery, async (newQuery) => {
  if (newQuery.length < 2) {
    searchResults.value = []
    return
  }
  
  // Mock search results - replace with actual API call
  const locale = route.params.locale || 'en'
  searchResults.value = [
    {
      id: 1,
      title: 'Summer Sale Campaign',
      subtitle: 'Email Campaign',
      icon: MegaphoneIcon,
      route: { name: 'campaigns.show', params: { id: 1, locale } }
    },
    {
      id: 2,
      title: 'Newsletter Subscribers',
      subtitle: 'Contact List',
      icon: UsersIcon,
      route: { name: 'contacts', params: { locale } }
    },
    {
      id: 3,
      title: 'Welcome Email Template',
      subtitle: 'Email Template',
      icon: DocumentDuplicateIcon,
      route: { name: 'templates', params: { locale } }
    }
  ].filter(item => 
    item.title.toLowerCase().includes(newQuery.toLowerCase()) ||
    item.subtitle.toLowerCase().includes(newQuery.toLowerCase())
  )
})

const hideSearchResults = () => {
  setTimeout(() => {
    showSearchResults.value = false
  }, 200)
}

const performSearch = () => {
  if (searchResults.value.length > 0) {
    navigateToResult(searchResults.value[0])
  }
}

const navigateToResult = (result) => {
  router.push(result.route)
  showSearchResults.value = false
  searchQuery.value = ''
}

const createCampaign = () => {
  const locale = route.params.locale || 'en'
  router.push({ name: 'campaigns.create', params: { locale } })
}
</script>

<style scoped>
.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.2s ease;
}

.slide-down-enter-from,
.slide-down-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>